import React, {FC, useRef, useState} from 'react';

import {KeyboardActionsEnum} from '../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {InboxModel} from '../../../../libs/shared/core/src/models/api/inboxModel';
import {CellsContext} from '../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ToolbarButton} from '../../../../libs/shared/ui/src/components/buttons/toolbarButton';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemIcon} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemIcon';
import {SimpleDropdown} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {PermissionDenied} from '../../../../srcCommon/permissions/permissionDenied';
import {ConversationBaseModel} from '../../../models/api/conversationModel';
import {VisualRecipientModel} from '../../../models/api/visualRecipientModel';
import {InboxesParameters} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {
  BlockRecipientDropdownItem,
  ReportPhishingDropdownItem,
} from '../viewer/conversation/actions/more/conversationsMoreDropdown';
import {
  ConversationsTrashButtonProps,
  ConversationsTrashCoordinatorModesEnum,
} from '../viewer/conversation/actions/trash/conversationsTrashCoordinator';
import {ConversationsTrashCoordinatorContainer} from '../viewer/conversation/actions/trash/conversationsTrashCoordinatorContainer';
import {makeInboxesTopbarSpamTooltipProps} from './inboxesTopbarCommon';
import {InboxesTopbarTooltip} from './inboxesTopbarTooltip';

/*
 * Props.
 */

interface InboxesTopbarSpamProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  inboxesParameters: InboxesParameters;
  conversationsInboxes: ReadonlyArray<InboxModel>;
  conversations: ReadonlyArray<ConversationBaseModel>;
  isBulkActionRequired: boolean;
  contextNamespaces: ReadonlyArray<string>;
  areAllInternal: boolean;
  recipient: VisualRecipientModel | undefined;
  onReportPhishingClick: (() => void) | undefined;
  onBlockRecipientClick: (() => void) | undefined;
  onUnblockRecipientClick: (() => void) | undefined;
}

/*
 * Component.
 */

export const InboxesTopbarSpam: FC<InboxesTopbarSpamProps> = (props) => {
  const theme = useUserTheme();
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  function renderButton(
    spamProps: ConversationsTrashButtonProps,
    ref: React.MutableRefObject<HTMLDivElement | null>,
  ) {
    const {permissionResult, areAllSpam} = spamProps;
    const tooltipProps = makeInboxesTopbarSpamTooltipProps(spamProps, props);
    const {onReportPhishingClick, onBlockRecipientClick, onUnblockRecipientClick} = props;

    const renderTooltip = () => (
      <InboxesTopbarTooltip
        keyboardAction={KeyboardActionsEnum.SPAM}
        firstLine={tooltipProps.firstLine || tooltipProps.mainButton}
        secondLine={tooltipProps.secondLine}
      />
    );
    function renderDropdown() {
      if (!isDropdownOpen) {
        return null;
      }

      return (
        <SimpleDropdown anchor={ref} onRequestClose={() => setIsDropdownOpen(false)} placement="bottom-end">
          <DropdownItem isDisabled={!spamProps.permissionResult.isAllowed} onSelect={spamProps.onClick}>
            <DropdownItemIcon
              iconName="spamSmall"
              label={tooltipProps.mainButton}
              iconSize={16}
              color={theme.greys.shade80}
            />
            <DropdownItemContent>
              <Trans id="conversation-actions-markasspam">Mark as spam</Trans>
            </DropdownItemContent>
          </DropdownItem>
          <ReportPhishingDropdownItem
            contextNamespaces={props.contextNamespaces}
            areAllInternal={props.areAllInternal}
            onReportPhishingClick={onReportPhishingClick}
            hasIcon
          />
          {props.recipient && (
            <BlockRecipientDropdownItem
              recipient={props.recipient}
              cellsContext={props.cellsContext}
              onBlockRecipientClick={onBlockRecipientClick}
              onUnblockRecipientClick={onUnblockRecipientClick}
              hasIcon
            />
          )}
        </SimpleDropdown>
      );
    }

    if (!permissionResult.isAllowed) {
      return (
        <>
          {renderDropdown()}
          <ToolbarButton
            noMargin
            ref={ref}
            isDisabled
            iconName="spam"
            label={<Trans id="toolbar-spam">Spam</Trans>}
            renderTooltip={() => <PermissionDenied reason={permissionResult.reason} />}
            placement="bottom"
          />
        </>
      );
    }

    // If the conversation is marked as spam, render the filled icon.
    if (areAllSpam) {
      return (
        <>
          {renderDropdown()}
          <ToolbarButton
            noMargin
            ref={ref}
            onClick={() => setIsDropdownOpen(true)}
            iconName="spamFilled"
            color={theme.palette.purple.shade40}
            hoverColor={theme.palette.purple.shade50}
            keyboardAction={KeyboardActionsEnum.SPAM}
            isTooltipMultiline
            label={<Trans id="toolbar-reopen">Reopen</Trans>}
            renderTooltip={renderTooltip}
            placement="bottom"
            hasHover={isDropdownOpen}
          />
        </>
      );
    }

    // Otherwise, render the normal icon.
    return (
      <>
        {renderDropdown()}
        <ToolbarButton
          noMargin
          ref={ref}
          onClick={() => setIsDropdownOpen(true)}
          iconName="spam"
          keyboardAction={KeyboardActionsEnum.SPAM}
          color={theme.greys.shade80}
          hoverColor={theme.greys.shade80}
          hasHover={isDropdownOpen}
          isTooltipMultiline
          label={<Trans id="toolbar-spam">Spam</Trans>}
          renderTooltip={renderTooltip}
          placement="bottom"
        />
      </>
    );
  }

  return (
    <ConversationsTrashCoordinatorContainer
      cellsContext={props.cellsContext}
      inboxesParameters={props.inboxesParameters}
      conversations={props.conversations}
      isBulkActionRequired={props.isBulkActionRequired}
      renderButton={(buttonProps) => renderButton(buttonProps, buttonRef)}
      panelId={props.panelId}
      mode={ConversationsTrashCoordinatorModesEnum.SPAM}
      skipTopbarPage
    />
  );
};

/*
 * Helpers.
 */
